import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../../context/SidebarContext';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { Box, Grid, Typography } from '@mui/material';
import VerificationIcon from '../../../assets/main/verification-icon.svg'
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import Loader from '../../../components/common/Loader';
import AlertPopup from '../../../components/common/AlertPopup';


import { 
  CustomAccordion, 
  PageCard, 
  ExpandIcon,
  CustomLink,
  VerificationContainer,
  VerificationImageContainer,
  RejectButton,
  AcceptButton,
  ButtonGroupContainer
} from './styles';


// redux
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';

import {
fetchFacilityDetails,
selectFacilityDetails,
updateFacilityStatus,
selectUpdateFacilityStatusLoading,
selectUpdateFacilityStatusError
} from '../../../modules/facility'
import FacilityProfileTab from '../../../components/FacilityProfileTab';


const FacilityReviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  const dispatch: AppDispatch = useDispatch()
  const navigate: ReturnType<typeof useNavigate> = useNavigate();
  const facilityDetails = useSelector(selectFacilityDetails);
  const updateStatusLoading = useSelector(selectUpdateFacilityStatusLoading);
  const updateStatusError = useSelector(selectUpdateFacilityStatusError);

  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [dialogTitle, setDialogTitle] = React.useState('');
  const [dialogMessage, setDialogMessage] = React.useState('');
  const [dialogAction, setDialogAction] = React.useState('');
  const [statusUpdateInitiated, setStatusUpdateInitiated] = React.useState(false);

  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState('');
  const [alertSeverity, setAlertSeverity] = React.useState<'success' | 'error'>('success');

  useEffect(() => {
    try{
      if (id) {
        const facilityId = parseInt(id)
        dispatch(fetchFacilityDetails(facilityId) as any);
      }
    }catch(err){
      console.log(err);
    }
  }, [dispatch, id])

  useEffect(() => {
    if (statusUpdateInitiated && !updateStatusLoading) {
      if (updateStatusError) {
        setAlertMessage(`Error updating facility status: ${updateStatusError}`);
        setAlertSeverity('error');
      } else {
        setAlertMessage(`Facility ${dialogAction}d successfully!`);
        setAlertSeverity('success');
        if (id) {
          const facilityId = parseInt(id);
          dispatch(fetchFacilityDetails(facilityId) as any);
        }
      }
      setAlertOpen(true);
      setStatusUpdateInitiated(false); // Reset after handling
    }
  }, [statusUpdateInitiated, updateStatusLoading, updateStatusError, dialogAction, dispatch, id]);

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const handleApproveClick = () => {
    setDialogTitle('Approve Facility');
    setDialogMessage('Are you sure you want to approve this facility?');
    setDialogAction('approve');
    setShowConfirmDialog(true);
  };

  const handleRejectClick = () => {
    setDialogTitle('Reject Facility');
    setDialogMessage('Are you sure you want to reject this facility?');
    setDialogAction('reject');
    setShowConfirmDialog(true);
  };

  const handleConfirm = () => {
    if (id) {
      setStatusUpdateInitiated(true); // Indicate that an update has been initiated
      if (dialogAction === 'approve') {
        dispatch(updateFacilityStatus({ facilityId: parseInt(id), action: 'approved' }) as any);
      } else if (dialogAction === 'reject') {
        dispatch(updateFacilityStatus({ facilityId: parseInt(id), action: 'rejected' }) as any);
      }
      setShowConfirmDialog(false); // Close dialog immediately after dispatching
    }
  };

  const getUserName = React.useCallback(() => {
    if(facilityDetails?.facility_name){
      return facilityDetails.facility_name
    }else{
      return "Facility Details"
    }
  }, [facilityDetails]);

  useEffect(() => {
    setActivePage('facilities');
    setPath([
      { title: 'Management', path: '/' },
      { title: 'Facility', path: '/facilities' },
      { title: getUserName(), path: '' },
    ]);
    setIsShow(true);
  }, [setActivePage, setPath, getUserName, setIsShow]);
  
  return (
    <Box>
      {facilityDetails &&  
        <PageCard>
          <FacilityProfileTab
            facilityName={facilityDetails.facility_name}
            id={facilityDetails.facility_id}
            status={facilityDetails.status}
          />
        </PageCard>
      }
      <PageCard>
        <CustomAccordion status='Pending' marginBottom={0}>
          <Grid container>
            <Grid size={6}>
              <Typography variant="subtitle1" fontWeight="bold">
                Basic Profile
              </Typography>
              <Typography variant='subtitle2' fontWeight="body2">
                The facility's basic details and documents
              </Typography>
            </Grid>
            <Grid size={6} >
              <CustomLink 
                flexShrink={0} 
                textAlign="right"
                onClick={() => {navigate(`/facility/${id}/onboarding/`)}}
              >
                Review the details <ExpandIcon />
              </CustomLink>
            </Grid>
          </Grid>
        </CustomAccordion>
      </PageCard>
      <PageCard>
        <VerificationContainer container>
          <Grid size={6}>
            <Grid container spacing={2} alignItems="center">
              <VerificationImageContainer size={1}>
                <img src={VerificationIcon} alt="Verification Icon" />
              </VerificationImageContainer>
              <Grid size={11}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Verification Action
                </Typography>
                <Typography variant='subtitle2' fontWeight="body2">
                  Confirm the worker's eligibility and finalize their status
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={6} >
            <Box flexShrink={0} textAlign="right">
              <ButtonGroupContainer>
                <RejectButton 
                  variant="outlined" 
                  onClick={handleRejectClick}
                  disabled={(facilityDetails?.viewed === false || facilityDetails?.status === 'rejected')}
                >
                  Reject
                </RejectButton>
                <AcceptButton 
                  variant="contained" 
                  onClick={handleApproveClick}
                  disabled={(facilityDetails?.viewed === false || facilityDetails?.status === 'approved')}
                >
                  Approve
                </AcceptButton>
              </ButtonGroupContainer>
            </Box>
          </Grid>
        </VerificationContainer>
      </PageCard>
      <ConfirmationDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title={dialogTitle}
        message={dialogMessage}
        onConfirm={handleConfirm}
      />
      <Loader open={updateStatusLoading} />
      <AlertPopup
        open={alertOpen}
        message={alertMessage}
        severity={alertSeverity}
        onClose={handleAlertClose}
      />
    </Box>
  );
};

export default FacilityReviewPage;
